/**
 * SachverstaendigenverfahrenTab — SV-Cockpit in der Akten-Detailansicht.
 *
 * Erscheint als eigener Reiter (URL-Section `svverfahren`) für Gutachten-Akten
 * mit Auftragstyp Sachverständigenverfahren. Zeigt:
 *  1. Fortschrittsbalken (Quelle: View v_sv_verfahren_fortschritt — NICHT im
 *     Frontend nachgerechnet) inkl. Phase n/7, naechste_aktion, Status.
 *  2. 7-Phasen-Stepper aus sv_phasen (Lucide-Icon je Name, optionale Phase
 *     markiert, aktuelle hervorgehoben, erledigte abgehakt).
 *  3. Phasen-Detail je Schritt: relevante sv_verfahren-Felder anzeigen/bearbeiten
 *     + passende Schreibvorlage (email_templates, Kategorie sv_verfahren) als
 *     „Entwurf erstellen" → email_drafts (status ready_for_review).
 *  4. Verlauf (sv_ereignisse) als Timeline.
 *  5. Ein-/ausklappbares Wissensbasis-Panel (sv_wissensbasis), kontextbezogen.
 *  6. Anlegen eines Verfahrens (Status `eroeffnet` + initiales sv_ereignisse).
 *  7. Statuswechsel schreibt automatisch einen sv_ereignisse-Eintrag.
 *
 * CI: Micro-Minimalismus, mobile-first, Koop-Red (#C41E3A) als Akzent,
 * graue Icons, koop-card-Stil (vgl. index.css). Keine externen Default-Modals.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FileSignature, MailOpen, UserPlus, UserCog, Users, Handshake, CheckCircle,
  CheckCircle2, Circle, Loader2, ChevronDown, ChevronRight, Clock, AlertTriangle,
  ScrollText, FileText, Mail, Gavel, Lightbulb, BookOpen, Plus, Save, Flag,
  CalendarClock, History, ArrowRight, XCircle,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Link } from 'wouter';
import { supabase } from '@/lib/supabase';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const KOOP_RED = '#C41E3A';

interface Props {
  akte: Record<string, any>;
  onChanged?: () => void;
}

type Row = Record<string, any>;

/* ════════════════════ Pure Helpers (exportiert für Tests) ════════════════════ */

export function formatEuro(val: number | string | null | undefined): string {
  if (val == null || val === '') return '—';
  const n = typeof val === 'string' ? Number(val) : val;
  if (Number.isNaN(n)) return '—';
  return n.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}

export function formatDatum(dateStr: string | null | undefined): string {
  if (!dateStr) return '—';
  try {
    return new Date(dateStr).toLocaleDateString('de-DE');
  } catch {
    return String(dateStr);
  }
}

/** Ersetzt {{platzhalter}} aus der Vorlage; unbekannte Platzhalter bleiben stehen. */
export function fuellePlatzhalter(text: string | null | undefined, vars: Record<string, string>): string {
  if (!text) return '';
  return text.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (match, key: string) => {
    const v = vars[key];
    return v != null && v !== '' ? v : match;
  });
}

export const SV_STATUS_CONFIG: Record<string, { label: string; classes: string }> = {
  eroeffnet:           { label: 'Eröffnet',            classes: 'bg-blue-100 text-blue-700' },
  benennung_vr_offen:  { label: 'Benennung VR offen',  classes: 'bg-amber-100 text-amber-700' },
  frist_abgelaufen:    { label: 'Frist abgelaufen',    classes: 'bg-orange-100 text-orange-700' },
  ersatzbenennung:     { label: 'Ersatzbenennung',     classes: 'bg-amber-100 text-amber-700' },
  sitzung_terminiert:  { label: 'Sitzung terminiert',  classes: 'bg-indigo-100 text-indigo-700' },
  einigung:            { label: 'Einigung',            classes: 'bg-emerald-100 text-emerald-700' },
  obmann_erforderlich: { label: 'Obmann erforderlich', classes: 'bg-purple-100 text-purple-700' },
  abgeschlossen:       { label: 'Abgeschlossen',       classes: 'bg-green-100 text-green-700' },
  gescheitert:         { label: 'Gescheitert',         classes: 'bg-red-100 text-red-700' },
};

export const SV_STATUS_REIHENFOLGE: string[] = [
  'eroeffnet', 'benennung_vr_offen', 'frist_abgelaufen', 'ersatzbenennung',
  'sitzung_terminiert', 'einigung', 'obmann_erforderlich', 'abgeschlossen', 'gescheitert',
];

/** Bildet einen Statuswechsel auf einen sv_ereignisse-Eintrag ab. */
export function statusZuEreignis(status: string): { typ: string; titel: string } {
  switch (status) {
    case 'eroeffnet':           return { typ: 'eroeffnung',       titel: 'Verfahren eröffnet' };
    case 'benennung_vr_offen':  return { typ: 'fristsetzung',     titel: 'Frist zur VR-Benennung gesetzt' };
    case 'frist_abgelaufen':    return { typ: 'fristablauf',      titel: 'Frist VR-Benennung abgelaufen' };
    case 'ersatzbenennung':     return { typ: 'ersatzbenennung',  titel: 'Ersatzbenennung VR-Ausschussmitglied' };
    case 'sitzung_terminiert':  return { typ: 'sitzung',          titel: 'Ausschusssitzung terminiert' };
    case 'einigung':            return { typ: 'einigung',         titel: 'Einigung erzielt' };
    case 'obmann_erforderlich': return { typ: 'obmann_benennung', titel: 'Obmann erforderlich' };
    case 'abgeschlossen':       return { typ: 'abschluss',        titel: 'Verfahren abgeschlossen' };
    case 'gescheitert':         return { typ: 'sonstiges',        titel: 'Verfahren gescheitert' };
    default:                    return { typ: 'sonstiges',        titel: 'Statuswechsel' };
  }
}

/** Lucide-Icon je sv_phasen.icon-Name. */
const PHASE_ICONS: Record<string, LucideIcon> = {
  'file-signature': FileSignature,
  'mail-open': MailOpen,
  'user-plus': UserPlus,
  'user-cog': UserCog,
  'users': Users,
  'handshake': Handshake,
  'check-circle': CheckCircle,
};

/** Icon + Farbe je sv_ereignis_typ. */
const EREIGNIS_ICONS: Record<string, { icon: LucideIcon; color: string }> = {
  eroeffnung:       { icon: MailOpen,     color: 'text-blue-500' },
  benennung_vn:     { icon: UserPlus,     color: 'text-blue-500' },
  benennung_vr:     { icon: UserPlus,     color: 'text-indigo-500' },
  fristsetzung:     { icon: CalendarClock, color: 'text-amber-500' },
  fristablauf:      { icon: AlertTriangle, color: 'text-orange-500' },
  ersatzbenennung:  { icon: UserCog,      color: 'text-amber-600' },
  mitglied_wechsel: { icon: UserCog,      color: 'text-amber-600' },
  obmann_benennung: { icon: Gavel,        color: 'text-purple-500' },
  sitzung:          { icon: Users,        color: 'text-indigo-500' },
  einigung:         { icon: Handshake,    color: 'text-emerald-600' },
  abschluss:        { icon: CheckCircle2, color: 'text-green-600' },
  schreiben_versand:{ icon: Mail,         color: 'text-gray-500' },
  sonstiges:        { icon: Flag,         color: 'text-gray-400' },
};

/**
 * Welche sv_wissensbasis-„ablauf"-Schritte (schritt_nr) sind je Phase relevant?
 * (Phasen 1–7 aus sv_phasen ↔ Ablaufschritte 1–9 der Wissensbasis.)
 */
export const PHASE_WISSENSBASIS_MAP: Record<number, number[]> = {
  1: [1, 2],
  2: [3, 5],
  3: [4],
  4: [],
  5: [6],
  6: [7, 8],
  7: [9],
};

/** Welche email_templates-Namen werden je Phase als „Entwurf erstellen" angeboten? */
export const PHASE_TEMPLATES: Record<number, string[]> = {
  2: ['SV – Eröffnungsschreiben an Versicherer'],
  4: ['SV – Ersatzbenennung Ausschussmitglied'],
  5: ['SV – Mitteilung Mitgliederwechsel an Versicherer'],
  7: ['SV – Protokoll-Anschreiben an Ausschussmitglied (Du)'],
};

/** Editierbare sv_verfahren-Felder je Phase. */
interface FeldDef { key: string; label: string; type: 'date' | 'text' | 'number' | 'bool' | 'textarea'; }
export const PHASE_FELDER: Record<number, FeldDef[]> = {
  1: [
    { key: 'rechtsgrundlage', label: 'Rechtsgrundlage', type: 'text' },
    { key: 'schadenart', label: 'Schadenart', type: 'text' },
  ],
  2: [
    { key: 'eroeffnung_am', label: 'Eröffnung am', type: 'date' },
    { key: 'benennung_frist_am', label: 'Frist VR-Benennung', type: 'date' },
  ],
  3: [
    { key: 'benennung_frist_am', label: 'Frist VR-Benennung', type: 'date' },
  ],
  4: [
    { key: 'benennung_frist_am', label: 'Frist (Widerspruch)', type: 'date' },
  ],
  5: [
    { key: 'sitzung_am', label: 'Sitzung am', type: 'date' },
    { key: 'sitzung_ort', label: 'Sitzungsort', type: 'text' },
  ],
  6: [
    { key: 'streitwert_vn', label: 'Streitwert VN (€)', type: 'number' },
    { key: 'streitwert_vr', label: 'Streitwert VR (€)', type: 'number' },
    { key: 'einigungsbetrag', label: 'Einigungsbetrag (€)', type: 'number' },
    { key: 'obmann_erforderlich', label: 'Obmann erforderlich', type: 'bool' },
    { key: 'gericht_zustaendig', label: 'Zuständiges Gericht', type: 'text' },
    { key: 'ergebnis', label: 'Ergebnis', type: 'textarea' },
  ],
  7: [
    { key: 'abschluss_am', label: 'Abschluss am', type: 'date' },
    { key: 'ergebnis', label: 'Ergebnis / Versandnotiz', type: 'textarea' },
  ],
};

const heute = () => new Date().toISOString().slice(0, 10);

/* ════════════════════════════════ Komponente ════════════════════════════════ */

export default function SachverstaendigenverfahrenTab({ akte, onChanged }: Props) {
  const akteId: string | undefined = akte?.id;

  const [loading, setLoading] = useState(true);
  const [verfahren, setVerfahren] = useState<Row | null>(null);
  const [fortschritt, setFortschritt] = useState<Row | null>(null);
  const [phasen, setPhasen] = useState<Row[]>([]);
  const [ereignisse, setEreignisse] = useState<Row[]>([]);
  const [wissen, setWissen] = useState<Row[]>([]);
  const [templates, setTemplates] = useState<Row[]>([]);

  const [selectedPhase, setSelectedPhase] = useState<number>(1);
  const [form, setForm] = useState<Row>({});
  const [saving, setSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draftBusy, setDraftBusy] = useState<string | null>(null);
  const [wissenOffen, setWissenOffen] = useState(false);

  const load = useCallback(async () => {
    if (!akteId) { setLoading(false); return; }
    setLoading(true);
    try {
      const [vRes, fRes, pRes, eRes, wRes, tRes] = await Promise.all([
        supabase.from('sv_verfahren').select('*').eq('akte_id', akteId).maybeSingle(),
        supabase.from('v_sv_verfahren_fortschritt').select('*').eq('akte_id', akteId).maybeSingle(),
        supabase.from('sv_phasen').select('*').order('phase_nr', { ascending: true }),
        supabase.from('sv_ereignisse').select('*').eq('akte_id', akteId)
          .order('ereignis_am', { ascending: true }).order('created_at', { ascending: true }),
        supabase.from('sv_wissensbasis').select('*').eq('aktiv', true)
          .order('sortierung', { ascending: true }),
        supabase.from('email_templates').select('*').eq('category', 'sv_verfahren'),
      ]);
      const v = (vRes.data as Row) ?? null;
      const f = (fRes.data as Row) ?? null;
      setVerfahren(v);
      setFortschritt(f);
      setPhasen((pRes.data as Row[]) ?? []);
      setEreignisse((eRes.data as Row[]) ?? []);
      setWissen((wRes.data as Row[]) ?? []);
      setTemplates((tRes.data as Row[]) ?? []);
      if (v) setForm({ ...v });
      const aktuell = (f?.aktuelle_phase_nr as number) || 1;
      setSelectedPhase(aktuell);
    } catch (e: any) {
      toast.error('SV-Daten konnten nicht geladen werden', { description: e?.message ?? String(e) });
    } finally {
      setLoading(false);
    }
  }, [akteId]);

  useEffect(() => { load(); }, [load]);

  const phasenSorted = useMemo(
    () => [...phasen].sort((a, b) => (a.phase_nr ?? 0) - (b.phase_nr ?? 0)),
    [phasen],
  );

  const aktuellePhase = (fortschritt?.aktuelle_phase_nr as number) || 0;
  const progress = Math.max(0, Math.min(100, Number(fortschritt?.progress_prozent ?? 0)));
  const istGescheitert = Boolean(fortschritt?.ist_gescheitert);
  const istAbgeschlossen = Boolean(fortschritt?.ist_abgeschlossen);
  const status: string = (verfahren?.status as string) || (fortschritt?.status as string) || 'eroeffnet';

  /* ── Verfahren anlegen ─────────────────────────────────────────────── */
  async function verfahrenAnlegen() {
    if (!akteId) return;
    setCreating(true);
    try {
      const { data: v, error } = await supabase.from('sv_verfahren').insert({
        akte_id: akteId,
        status: 'eroeffnet',
        rechtsgrundlage: '§ 84 VVG i.V.m. AKB (Sachverständigenverfahren)',
        schadenart: akte.schadenart ?? null,
        eroeffnung_am: heute(),
      }).select('*').single();
      if (error) throw error;
      const vId = (v as Row).id;
      await supabase.from('sv_ereignisse').insert({
        sv_verfahren_id: vId,
        akte_id: akteId,
        ereignis_am: heute(),
        typ: 'eroeffnung',
        titel: 'Verfahren eröffnet',
        beschreibung: 'Sachverständigenverfahren über das SV-Cockpit angelegt.',
      });
      toast.success('Sachverständigenverfahren eröffnet.');
      onChanged?.();
      await load();
    } catch (e: any) {
      toast.error('Anlegen fehlgeschlagen', { description: e?.message ?? String(e) });
    } finally {
      setCreating(false);
    }
  }

  /* ── Phasen-Felder speichern ───────────────────────────────────────── */
  async function speichern() {
    if (!verfahren?.id) return;
    setSaving(true);
    try {
      const felder = PHASE_FELDER[selectedPhase] ?? [];
      const patch: Row = {};
      for (const f of felder) {
        let v = form[f.key];
        if (f.type === 'number') v = v === '' || v == null ? null : Number(v);
        if (f.type === 'date') v = v || null;
        if (f.type === 'text' || f.type === 'textarea') v = v ?? null;
        if (f.type === 'bool') v = Boolean(v);
        patch[f.key] = v;
      }
      const { error } = await supabase.from('sv_verfahren').update(patch).eq('id', verfahren.id);
      if (error) throw error;
      toast.success('Gespeichert.');
      onChanged?.();
      await load();
    } catch (e: any) {
      toast.error('Speichern fehlgeschlagen', { description: e?.message ?? String(e) });
    } finally {
      setSaving(false);
    }
  }

  /* ── Statuswechsel (schreibt sv_ereignisse) ────────────────────────── */
  async function statusWechseln(neu: string) {
    if (!verfahren?.id || neu === status) return;
    setStatusSaving(true);
    try {
      const { error } = await supabase.from('sv_verfahren').update({ status: neu }).eq('id', verfahren.id);
      if (error) throw error;
      const ev = statusZuEreignis(neu);
      await supabase.from('sv_ereignisse').insert({
        sv_verfahren_id: verfahren.id,
        akte_id: akteId,
        ereignis_am: heute(),
        typ: ev.typ,
        titel: ev.titel,
        beschreibung: 'Statuswechsel über das SV-Cockpit.',
      });
      toast.success(`Status: ${SV_STATUS_CONFIG[neu]?.label ?? neu}`);
      onChanged?.();
      await load();
    } catch (e: any) {
      toast.error('Statuswechsel fehlgeschlagen', { description: e?.message ?? String(e) });
    } finally {
      setStatusSaving(false);
    }
  }

  /* ── Entwurf aus Vorlage erstellen ─────────────────────────────────── */
  const platzhalterWerte = useMemo<Record<string, string>>(() => {
    const fahrzeug = [akte.fahrzeug_marke, akte.fahrzeug_modell].filter(Boolean).join(' ');
    return {
      vn_name: (fortschritt?.kunde_name as string) || akte.anspruchsteller_name || akte.kunde_name || '',
      versicherung: akte.versicherung || '',
      schadennummer: akte.schadennummer || '',
      fahrzeug,
      kennzeichen: akte.kennzeichen || akte.fahrzeug_kennzeichen || '',
      rechtsgrundlage: (verfahren?.rechtsgrundlage as string) || '',
      benennung_frist_am: formatDatum(verfahren?.benennung_frist_am as string),
      sitzung_am: formatDatum(verfahren?.sitzung_am as string),
    };
  }, [akte, fortschritt, verfahren]);

  async function entwurfErstellen(templateName: string) {
    if (!akteId) return;
    const tpl = templates.find((t) => t.name === templateName);
    if (!tpl) { toast.error('Vorlage nicht gefunden: ' + templateName); return; }
    setDraftBusy(templateName);
    try {
      const subject = fuellePlatzhalter(tpl.subject, platzhalterWerte);
      const bodyText = fuellePlatzhalter(tpl.body_text, platzhalterWerte);
      const bodyHtml = tpl.body_html ? fuellePlatzhalter(tpl.body_html, platzhalterWerte) : null;
      // Protokoll-Anschreiben geht an das Ausschussmitglied (unbekannte Adresse → leer),
      // alle übrigen Vorlagen an den Versicherer.
      const anVersicherer = templateName !== 'SV – Protokoll-Anschreiben an Ausschussmitglied (Du)';
      const recipient = anVersicherer ? (akte.versicherung_email || '') : '';
      const { error } = await supabase.from('email_drafts').insert({
        akte_id: akteId,
        recipient,
        subject,
        body_text: bodyText,
        body_html: bodyHtml,
        status: 'ready_for_review',
        created_by_agent_id: 'sv-verfahren-ui',
        review_note: `SV-Verfahren: Entwurf aus Vorlage „${templateName}".`,
      });
      if (error) throw error;
      toast.success('Entwurf erstellt – liegt in der Freigabe.', {
        description: recipient ? `Empfänger: ${recipient}` : 'Empfänger bitte in der Freigabe ergänzen.',
      });
    } catch (e: any) {
      toast.error('Entwurf fehlgeschlagen', { description: e?.message ?? String(e) });
    } finally {
      setDraftBusy(null);
    }
  }

  /* ──────────────────────────────── Render ─────────────────────────── */

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" /> SV-Verfahren wird geladen…
      </div>
    );
  }

  if (!verfahren) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6 max-w-xl">
        <div className="flex items-center gap-2 mb-2">
          <Gavel className="w-5 h-5" style={{ color: KOOP_RED }} />
          <h2 className="text-base font-semibold text-gray-800">Sachverständigenverfahren</h2>
        </div>
        <p className="text-sm text-gray-500 mb-4">
          Für diese Akte ist noch kein Sachverständigenverfahren angelegt. Eröffne das Verfahren,
          um Fortschritt, Ausschuss, Sitzung, Einigung und Protokoll hier zu steuern.
        </p>
        <button
          type="button"
          onClick={verfahrenAnlegen}
          disabled={creating}
          className="inline-flex items-center gap-2 text-sm font-semibold text-white px-4 py-2 rounded-lg disabled:opacity-50"
          style={{ backgroundColor: KOOP_RED }}
          data-testid="sv-verfahren-anlegen"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Sachverständigenverfahren eröffnen
        </button>
      </div>
    );
  }

  const statusCfg = SV_STATUS_CONFIG[status] ?? { label: status, classes: 'bg-gray-100 text-gray-700' };
  const barColor = istGescheitert ? '#dc2626' : istAbgeschlossen ? '#16a34a' : KOOP_RED;
  const headBorder = istGescheitert ? 'border-red-200 bg-red-50' : istAbgeschlossen ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white';

  const phaseDef = phasenSorted.find((p) => p.phase_nr === selectedPhase);
  const felder = PHASE_FELDER[selectedPhase] ?? [];
  const phaseTemplates = (PHASE_TEMPLATES[selectedPhase] ?? []).filter((n) => templates.some((t) => t.name === n));

  return (
    <div className="space-y-4" data-testid="sv-verfahren-tab">

      {/* ───── 1. Fortschritts-Header ───── */}
      <div className={cn('rounded-xl border p-4', headBorder)}>
        <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
          <div className="flex items-center gap-2">
            <Gavel className="w-5 h-5" style={{ color: barColor }} />
            <h2 className="text-base font-semibold text-gray-800">Sachverständigenverfahren</h2>
            <span className={cn('inline-flex items-center text-[10px] font-semibold px-2 py-0.5 rounded-full', statusCfg.classes)}>
              {statusCfg.label}
            </span>
          </div>
          {/* Statuswechsel */}
          <label className="flex items-center gap-2 text-[11px] text-gray-500">
            Status
            <select
              value={status}
              disabled={statusSaving}
              onChange={(e) => statusWechseln(e.target.value)}
              className="text-[12px] border border-gray-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-1"
              data-testid="sv-status-select"
            >
              {SV_STATUS_REIHENFOLGE.map((s) => (
                <option key={s} value={s}>{SV_STATUS_CONFIG[s]?.label ?? s}</option>
              ))}
            </select>
            {statusSaving && <Loader2 className="w-3.5 h-3.5 animate-spin text-gray-400" />}
          </label>
        </div>

        {/* Balken */}
        <div className="flex items-center gap-3">
          <div className="flex-1 h-2.5 rounded-full bg-gray-200 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, backgroundColor: barColor }}
              data-testid="sv-progress-bar"
            />
          </div>
          <span className="text-sm font-semibold text-gray-700 tabular-nums" data-testid="sv-progress-prozent">
            {progress}%
          </span>
          <span className="text-[11px] text-gray-400 whitespace-nowrap">
            Phase {aktuellePhase || '—'} / 7
          </span>
        </div>

        {/* naechste_aktion */}
        {fortschritt?.naechste_aktion && !istAbgeschlossen && (
          <div className="mt-2 flex items-start gap-1.5 text-[12px] text-gray-600">
            <ArrowRight className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: barColor }} />
            <span><span className="font-medium">Nächste Aktion:</span> {fortschritt.naechste_aktion}</span>
          </div>
        )}
        {istAbgeschlossen && (
          <div className="mt-2 flex items-center gap-1.5 text-[12px] text-green-700">
            <CheckCircle2 className="w-3.5 h-3.5" /> Verfahren abgeschlossen{verfahren.abschluss_am ? ` am ${formatDatum(verfahren.abschluss_am)}` : ''}.
          </div>
        )}
        {istGescheitert && (
          <div className="mt-2 flex items-center gap-1.5 text-[12px] text-red-700">
            <XCircle className="w-3.5 h-3.5" /> Verfahren gescheitert – Durchsetzung ggf. gerichtlich.
          </div>
        )}
      </div>

      {/* ───── 2. Stepper ───── */}
      <div className="bg-white rounded-xl border border-gray-200 p-3">
        <div className="flex items-stretch gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {phasenSorted.map((p) => {
            const nr = p.phase_nr as number;
            const Icon = PHASE_ICONS[p.icon as string] || Circle;
            const isCurrent = nr === aktuellePhase;
            const isDone = aktuellePhase > 0 && nr < aktuellePhase && !istGescheitert;
            const isSelected = nr === selectedPhase;
            return (
              <button
                key={p.id}
                type="button"
                onClick={() => setSelectedPhase(nr)}
                title={p.beschreibung || p.label}
                className={cn(
                  'flex-shrink-0 flex flex-col items-start gap-1 rounded-lg border px-3 py-2 min-w-[120px] text-left transition-all',
                  isSelected ? 'border-transparent text-white shadow-sm' : 'border-gray-200 hover:bg-gray-50',
                )}
                style={isSelected ? { backgroundColor: KOOP_RED } : undefined}
                data-testid={`sv-phase-step-${nr}`}
              >
                <div className="flex items-center gap-1.5 w-full">
                  <span className={cn(
                    'flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold flex-shrink-0',
                    isSelected ? 'bg-white/25 text-white' : isDone ? 'bg-green-100 text-green-600' : isCurrent ? 'text-white' : 'bg-gray-100 text-gray-500',
                  )}
                  style={isCurrent && !isSelected ? { backgroundColor: KOOP_RED } : undefined}>
                    {isDone ? <CheckCircle2 className="w-3.5 h-3.5" /> : nr}
                  </span>
                  <Icon className={cn('w-3.5 h-3.5 flex-shrink-0', isSelected ? 'text-white' : 'text-gray-400')} />
                </div>
                <span className="text-[11px] font-medium leading-tight">{p.label}</span>
                {p.ist_optional && (
                  <span className={cn('text-[9px] uppercase tracking-wide', isSelected ? 'text-white/80' : 'text-gray-400')}>
                    optional
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* ───── 3. Phasen-Detail ───── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          {(() => {
            const Icon = phaseDef ? (PHASE_ICONS[phaseDef.icon as string] || Circle) : Circle;
            return <Icon className="w-4 h-4 text-gray-500" />;
          })()}
          <h3 className="text-sm font-semibold text-gray-800">
            Phase {selectedPhase}: {phaseDef?.label ?? '—'}
          </h3>
          {phaseDef?.ist_optional && (
            <span className="text-[9px] uppercase tracking-wide text-gray-400 border border-gray-200 rounded px-1.5 py-0.5">optional</span>
          )}
        </div>
        {phaseDef?.beschreibung && (
          <p className="text-[12px] text-gray-500 mb-3">{phaseDef.beschreibung}</p>
        )}

        {felder.length > 0 ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {felder.map((f) => (
                <div key={f.key} className={f.type === 'textarea' ? 'sm:col-span-2' : ''}>
                  <label className="block text-[11px] font-medium text-gray-500 mb-1">{f.label}</label>
                  {f.type === 'textarea' ? (
                    <textarea
                      value={form[f.key] ?? ''}
                      onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                      rows={3}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1"
                    />
                  ) : f.type === 'bool' ? (
                    <label className="flex items-center gap-2 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(form[f.key])}
                        onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.checked }))}
                        className="w-4 h-4 rounded border-gray-300"
                      />
                      {form[f.key] ? 'Ja' : 'Nein'}
                    </label>
                  ) : (
                    <input
                      type={f.type === 'number' ? 'number' : f.type === 'date' ? 'date' : 'text'}
                      value={form[f.key] ?? ''}
                      onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                      className="w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-1"
                    />
                  )}
                </div>
              ))}
            </div>
            <div className="mt-3 flex items-center gap-2">
              <button
                type="button"
                onClick={speichern}
                disabled={saving}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-3 py-1.5 rounded-lg disabled:opacity-50"
                style={{ backgroundColor: KOOP_RED }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Speichern
              </button>
            </div>
          </>
        ) : (
          <p className="text-[12px] text-gray-400">Für diese Phase sind keine direkt editierbaren Felder hinterlegt.</p>
        )}

        {/* Phase 6 Kurzübersicht der Beträge */}
        {selectedPhase === 6 && (
          <div className="mt-4 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-gray-50 p-2">
              <div className="text-[10px] text-gray-400 uppercase tracking-wide">Forderung VN</div>
              <div className="text-sm font-semibold text-gray-700">{formatEuro(verfahren.streitwert_vn)}</div>
            </div>
            <div className="rounded-lg bg-gray-50 p-2">
              <div className="text-[10px] text-gray-400 uppercase tracking-wide">Angebot VR</div>
              <div className="text-sm font-semibold text-gray-700">{formatEuro(verfahren.streitwert_vr)}</div>
            </div>
            <div className="rounded-lg p-2" style={{ backgroundColor: '#FCE8EC' }}>
              <div className="text-[10px] uppercase tracking-wide" style={{ color: KOOP_RED }}>Einigung</div>
              <div className="text-sm font-semibold" style={{ color: KOOP_RED }}>{formatEuro(verfahren.einigungsbetrag)}</div>
            </div>
          </div>
        )}

        {/* Schreibvorlagen → Entwurf */}
        {phaseTemplates.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">Schreiben</div>
            <div className="flex flex-wrap gap-2">
              {phaseTemplates.map((name) => (
                <button
                  key={name}
                  type="button"
                  onClick={() => entwurfErstellen(name)}
                  disabled={draftBusy === name}
                  className="inline-flex items-center gap-1.5 text-[12px] font-medium text-gray-700 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 disabled:opacity-50"
                  data-testid="sv-entwurf-btn"
                >
                  {draftBusy === name ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Mail className="w-3.5 h-3.5 text-gray-400" />}
                  Entwurf: {name.replace(/^SV – /, '')}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-[11px] text-gray-400">Platzhalter werden aus Akte/Verfahren gefüllt; Entwurf landet in der Freigabe.</p>
          </div>
        )}
      </div>

      {/* ───── 4. Timeline ───── */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 mb-3">
          <History className="w-4 h-4 text-gray-500" />
          <h3 className="text-sm font-semibold text-gray-800">Verlauf</h3>
          <span className="text-[11px] text-gray-400">({ereignisse.length})</span>
        </div>
        {ereignisse.length === 0 ? (
          <p className="text-[12px] text-gray-400">Noch keine Ereignisse erfasst.</p>
        ) : (
          <ol className="relative border-l border-gray-200 ml-2">
            {ereignisse.map((e) => {
              const cfg = EREIGNIS_ICONS[e.typ as string] || EREIGNIS_ICONS.sonstiges;
              const Icon = cfg.icon;
              return (
                <li key={e.id} className="mb-4 ml-4 last:mb-0">
                  <span className="absolute -left-[9px] flex items-center justify-center w-[18px] h-[18px] rounded-full bg-white ring-2 ring-white">
                    <Icon className={cn('w-4 h-4', cfg.color)} />
                  </span>
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-[13px] font-medium text-gray-800">{e.titel}</span>
                    <time className="text-[11px] text-gray-400">{formatDatum(e.ereignis_am)}</time>
                  </div>
                  {e.beschreibung && <p className="text-[12px] text-gray-500 mt-0.5">{e.beschreibung}</p>}
                </li>
              );
            })}
          </ol>
        )}
      </div>

      {/* ───── 5. Wissensbasis ───── */}
      <WissensbasisPanel
        wissen={wissen}
        phaseNr={selectedPhase}
        offen={wissenOffen}
        onToggle={() => setWissenOffen((o) => !o)}
      />
    </div>
  );
}

/* ════════════════════════════ Wissensbasis-Panel ════════════════════════════ */

function WissensbasisPanel({
  wissen, phaseNr, offen, onToggle,
}: { wissen: Row[]; phaseNr: number; offen: boolean; onToggle: () => void }) {
  const relevanteSchritte = PHASE_WISSENSBASIS_MAP[phaseNr] ?? [];
  const kontext = useMemo(() => {
    let ctx = wissen.filter((w) => w.kategorie === 'ablauf' && relevanteSchritte.includes(w.schritt_nr));
    if (ctx.length === 0) ctx = wissen.filter((w) => w.kategorie === 'eskalation');
    return ctx;
  }, [wissen, relevanteSchritte]);

  const gruppen = useMemo(() => {
    const order = ['ablauf', 'eskalation', 'optimierung', 'grundlage', 'kostenrisiko'];
    const map: Record<string, Row[]> = {};
    for (const w of wissen) (map[w.kategorie] ||= []).push(w);
    return order.filter((k) => map[k]?.length).map((k) => ({ kategorie: k, items: map[k] }));
  }, [wissen]);

  const KAT_LABEL: Record<string, string> = {
    ablauf: 'Ablauf', eskalation: 'Eskalation', optimierung: 'Optimierung',
    grundlage: 'Grundlage', kostenrisiko: 'Kostenrisiko',
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-4 py-3 hover:bg-gray-50"
        data-testid="sv-wissensbasis-toggle"
      >
        <BookOpen className="w-4 h-4 text-gray-500" />
        <span className="text-sm font-semibold text-gray-800 flex-1 text-left">Wissensbasis &amp; Tipps</span>
        {offen ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
      </button>

      {/* Kontextbezogene Tipps zur aktuellen Phase – immer sichtbar */}
      {kontext.length > 0 && (
        <div className="px-4 pb-3 -mt-1">
          {kontext.map((w) => (
            <div key={w.id} className="flex items-start gap-2 rounded-lg p-2" style={{ backgroundColor: '#FCE8EC' }}>
              <Lightbulb className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: KOOP_RED }} />
              <div>
                <div className="text-[12px] font-semibold text-gray-800">{w.titel}</div>
                {w.inhalt && <div className="text-[12px] text-gray-600 mt-0.5 whitespace-pre-line">{w.inhalt}</div>}
                {(w.frist || w.ausloeser) && (
                  <div className="text-[11px] text-gray-400 mt-1 flex gap-3 flex-wrap">
                    {w.ausloeser && <span><Clock className="w-3 h-3 inline mr-1" />{w.ausloeser}</span>}
                    {w.frist && <span><Flag className="w-3 h-3 inline mr-1" />Frist: {w.frist}</span>}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {offen && (
        <div className="px-4 pb-4 space-y-4 border-t border-gray-100 pt-3">
          {gruppen.map((g) => (
            <div key={g.kategorie}>
              <div className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">{KAT_LABEL[g.kategorie] ?? g.kategorie}</div>
              <ul className="space-y-2">
                {g.items.map((w) => (
                  <li key={w.id} className="text-[12px]">
                    <span className="font-medium text-gray-800">{w.titel}</span>
                    {w.inhalt && <span className="text-gray-500"> — {w.inhalt}</span>}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
